import { useId, type InputHTMLAttributes, type ReactNode } from 'react'

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode
  /** Shown under the control; announced with it. */
  hint?: ReactNode
  /** Sets aria-invalid and renders the message. */
  error?: string | null
  /** Mono, wide-tracked, centred — for a verification code. */
  code?: boolean
}

/**
 * A labelled input.
 *
 * The label is a real <label for>, and the hint and error are wired through
 * `aria-describedby` so a screen reader reads the message with the field
 * rather than leaving it stranded. `aria-invalid` is what a validation error
 * actually means to assistive tech; red text alone is not.
 */
export function Field({ label, hint, error, code = false, className, ...rest }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <label className="field__label" htmlFor={id}>{label}</label>
      <input
        {...rest}
        id={id}
        className={`field__control${code ? ' field__control--code' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint && <span className="field__hint" id={hintId}>{hint}</span>}
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  )
}
